/**
 * src/engine/collateral.ts — collateral-bucket aggregation, the liquidation order
 * (§6.22), the shared forced-liquidation cure (§6.7), and the instantaneous stress
 * snapshot (§6.23). The single-portfolio case is just one bucket, so the projection
 * runs this same code for both modes.
 */
import type { AssetClass, LiquidationPolicy, StressSnapshotResult } from '@/types';
import type { GainsTaxModel } from './gainsTax';
import { ASSET_CLASS_DEFAULTS } from './assetClasses';
import { basisConsumedOnSale, gainFraction, realizedTaxOnSale } from './tax';

/** Epsilon for currency/LTV boundary comparisons (§8.3). */
export const EPS = 1e-6;

/** Mutable per-bucket state carried through the monthly loop and cure. */
export interface BucketState {
  assetClass: AssetClass;
  value: number;
  basis: number;
  advanceRate: number;
  eligible: boolean;
  expectedReturnAnnualPct: number;
  volatilityPct: number;
  dividendYieldAnnualPct: number;
}

/** Effective advance rate: ineligible buckets contribute 0 to LVC (§6.21). */
export function effectiveAdvanceRate(b: BucketState): number {
  return b.eligible ? b.advanceRate : 0;
}

export function aggregatePortfolioValue(states: BucketState[]): number {
  return states.reduce((sum, b) => sum + b.value, 0);
}

/** LVC = Σ (value × effective advance rate) (§6.21). */
export function aggregateLVC(states: BucketState[]): number {
  return states.reduce((sum, b) => sum + b.value * effectiveAdvanceRate(b), 0);
}

/** Derived read-only blended advance rate = LVC / portfolio (§6.21). */
export function blendedAdvanceRate(states: BucketState[]): number {
  const pv = aggregatePortfolioValue(states);
  return pv > 0 ? aggregateLVC(states) / pv : 0;
}

/** Value-weighted expected return (§6.21). */
export function weightedReturnPct(states: BucketState[]): number {
  const pv = aggregatePortfolioValue(states);
  if (pv <= 0) return 0;
  return states.reduce((sum, b) => sum + b.value * b.expectedReturnAnnualPct, 0) / pv;
}

function liquidityRank(b: BucketState): number {
  return ASSET_CLASS_DEFAULTS[b.assetClass].liquidityRank;
}

/**
 * Order bucket indices for liquidation under the active policy (§6.22). pro_rata has no
 * meaningful order (the cure sells proportionally) and returns natural order.
 */
export function liquidationOrder(states: BucketState[], policy: LiquidationPolicy): number[] {
  const idx = states.map((_, i) => i);
  const cmp = (a: number, b: number): number => {
    const A = states[a]!;
    const B = states[b]!;
    switch (policy) {
      case 'schwab_liquidity': {
        const deadA = effectiveAdvanceRate(A) <= EPS ? 0 : 1;
        const deadB = effectiveAdvanceRate(B) <= EPS ? 0 : 1;
        if (deadA !== deadB) return deadA - deadB;
        if (liquidityRank(A) !== liquidityRank(B)) return liquidityRank(A) - liquidityRank(B);
        return effectiveAdvanceRate(A) - effectiveAdvanceRate(B);
      }
      case 'most_tax_efficient': {
        const gA = gainFraction(A.basis, A.value);
        const gB = gainFraction(B.basis, B.value);
        if (Math.abs(gA - gB) > EPS) return gA - gB;
        return effectiveAdvanceRate(A) - effectiveAdvanceRate(B);
      }
      case 'lowest_advance_first':
        return effectiveAdvanceRate(A) - effectiveAdvanceRate(B);
      case 'preserve_high_advance': {
        if (Math.abs(effectiveAdvanceRate(A) - effectiveAdvanceRate(B)) > EPS) {
          return effectiveAdvanceRate(A) - effectiveAdvanceRate(B);
        }
        return liquidityRank(A) - liquidityRank(B);
      }
      case 'pro_rata':
        return a - b;
    }
  };
  return idx.sort(cmp);
}

export interface CureResult {
  forcedSale: number;
  realizedTax: number;
  /** Loan balance after the cure (collateral sold, proceeds applied). */
  balance: number;
  /** True if the call could not be cured (collateral exhausted) — insolvency (§17). */
  insolvent: boolean;
}

/** Is a call active? balance > LVC (ltv > 1) or LVC < minimumLVC (§6.7). */
export function isMaintenanceCall(balance: number, lvc: number, minimumLVC: number): boolean {
  return balance > lvc + EPS || lvc < minimumLVC - EPS;
}

/**
 * Cure a maintenance call by selling collateral in policy order until ltv ≤ targetLtv
 * or collateral is exhausted (§6.7). Mutates `states` (values/basis fall). Each bucket's
 * own gain fraction drives realized tax; net proceeds pay down the loan.
 */
export function cureMaintenanceCall(
  states: BucketState[],
  balance: number,
  targetLtv: number,
  combinedRate: number,
  policy: LiquidationPolicy,
): CureResult {
  if (policy === 'pro_rata') {
    return cureProRata(states, balance, targetLtv, combinedRate);
  }
  let forcedSale = 0;
  let realizedTax = 0;
  let lvc = aggregateLVC(states);
  const order = liquidationOrder(states, policy);
  for (const i of order) {
    if (balance <= targetLtv * lvc + EPS) break;
    const b = states[i]!;
    if (b.value <= EPS) continue;
    const g = gainFraction(b.basis, b.value);
    const a = effectiveAdvanceRate(b);
    const netFactor = 1 - g * combinedRate;
    const denom = netFactor - targetLtv * a;
    let s: number;
    if (denom > EPS) {
      s = Math.min((balance - targetLtv * lvc) / denom, b.value);
    } else {
      // Selling this bucket can't reach target on its own; liquidate it fully, continue.
      s = b.value;
    }
    realizedTax += realizedTaxOnSale(s, g, combinedRate);
    balance = Math.max(0, balance - s * netFactor);
    forcedSale += s;
    b.value -= s;
    b.basis = Math.max(0, b.basis - basisConsumedOnSale(s, g));
    lvc = aggregateLVC(states);
  }
  return { forcedSale, realizedTax, balance, insolvent: balance > targetLtv * lvc + EPS };
}

/** pro_rata cure: sell proportionally across all buckets in a single closed-form step. */
function cureProRata(
  states: BucketState[],
  balance: number,
  targetLtv: number,
  combinedRate: number,
): CureResult {
  const lvc = aggregateLVC(states);
  const totalValue = aggregatePortfolioValue(states);
  if (totalValue <= EPS) {
    return { forcedSale: 0, realizedTax: 0, balance, insolvent: balance > EPS };
  }
  let netFactorAgg = 0;
  let aAgg = 0;
  for (const b of states) {
    const g = gainFraction(b.basis, b.value);
    netFactorAgg += (b.value / totalValue) * (1 - g * combinedRate);
    aAgg += (b.value / totalValue) * effectiveAdvanceRate(b);
  }
  const denom = netFactorAgg - targetLtv * aAgg;
  const target = denom > EPS ? (balance - targetLtv * lvc) / denom : totalValue;
  const S = Math.min(Math.max(0, target), totalValue);
  let realizedTax = 0;
  for (const b of states) {
    const share = b.value / totalValue;
    const s = S * share;
    const g = gainFraction(b.basis, b.value);
    realizedTax += realizedTaxOnSale(s, g, combinedRate);
    balance = Math.max(0, balance - s * (1 - g * combinedRate));
    b.value -= s;
    b.basis = Math.max(0, b.basis - basisConsumedOnSale(s, g));
  }
  const newLvc = aggregateLVC(states);
  return { forcedSale: S, realizedTax, balance, insolvent: balance > targetLtv * newLvc + EPS };
}

/**
 * Cure a call under a `GainsTaxModel` (§6.12b). The two closed-form solves above need ONE
 * scalar rate, but a bracketed sale crosses rate boundaries partway through, so the right
 * scalar is the AVERAGE rate over the gain the sale actually realizes — which is not known
 * until the sale is solved. Trial-solve on cloned state, read the gain, recompute the
 * average rate, repeat. The rate range is narrow (0 to ~0.238) and the solve is monotone
 * in it, so this converges in a couple of passes; the flat model converges on the first,
 * since its rate does not move.
 */
export function cureWithModel(
  states: BucketState[],
  balance: number,
  targetLtv: number,
  model: GainsTaxModel,
  policy: LiquidationPolicy,
  iterations = 3,
): CureResult {
  let rate = model.rateHint();
  let gain = 0;
  for (let i = 0; i < iterations; i++) {
    const trial = cureMaintenanceCall(cloneBuckets(states), balance, targetLtv, rate, policy);
    gain = gainOfSale(states, trial.forcedSale);
    const next = gain > 0 ? model.taxOn(gain) / gain : model.rateHint();
    if (Math.abs(next - rate) <= EPS) break;
    rate = next;
  }
  const result = cureMaintenanceCall(states, balance, targetLtv, rate, policy);
  // Record the realized gain so later sales in the same tax year stack on top of it.
  // Derived from the pre-mutation states, never as tax/rate — that division blows up in
  // the 0% bracket, where the tax is legitimately zero.
  model.realize(gain);
  return result;
}

/** Gain embedded in selling `grossSale` across `states` in value-weighted proportion. */
function gainOfSale(states: BucketState[], grossSale: number): number {
  const total = aggregatePortfolioValue(states);
  if (total <= EPS || grossSale <= 0) return 0;
  const blendedGain = states.reduce(
    (sum, b) => sum + (b.value / total) * gainFraction(b.basis, b.value),
    0,
  );
  return Math.min(grossSale, total) * blendedGain;
}

// ───────────────────────────── stress snapshot (§6.23) ─────────────────────────────

export interface StressSnapshotParams {
  /** Bucket states at `atMonth` (values/basis already grown to that month). */
  buckets: BucketState[];
  /** Loan balance at `atMonth` — held FIXED during the price move (§6.23). */
  balance: number;
  atMonth: number;
  minimumLVC: number;
  combinedRate: number;
  cureTargetLtv: number;
  policy: LiquidationPolicy;
  /** Single global decline in percent (single-portfolio mode). */
  globalDropPct?: number;
  /** Per-class declines in percent (bucket mode); negative = a gain. */
  dropByClass?: Partial<Record<AssetClass, number>>;
}

function cloneBuckets(states: BucketState[]): BucketState[] {
  return states.map((b) => ({ ...b }));
}

/** Resolve the drop percent for a bucket from the global or per-class inputs. */
function dropPctFor(
  b: BucketState,
  globalDropPct: number | undefined,
  dropByClass: Partial<Record<AssetClass, number>> | undefined,
): number {
  if (dropByClass) {
    const d = dropByClass[b.assetClass];
    if (d !== undefined) return d;
  }
  return globalDropPct ?? 0;
}

/**
 * "If the market dropped right now, where would I stand?" (§6.23). Pure and
 * non-destructive: clones the buckets, applies the drop, recomputes the collateral-side
 * figures (balance and APR/tier are unchanged), and — if a call triggers — reuses the
 * cure to show the forced-sale/tax consequence.
 */
export function stressSnapshot(params: StressSnapshotParams): StressSnapshotResult {
  const { balance, atMonth, minimumLVC, combinedRate, cureTargetLtv, policy } = params;
  const preStressLVC = aggregateLVC(params.buckets);

  const stressed = cloneBuckets(params.buckets);
  for (const b of stressed) {
    const d = dropPctFor(b, params.globalDropPct, params.dropByClass);
    b.value = Math.max(0, b.value * (1 - d / 100));
  }

  const stressedLVC = aggregateLVC(stressed);
  const stressedPortfolio = aggregatePortfolioValue(stressed);
  const ltv = stressedLVC > 0 ? balance / stressedLVC : Infinity;
  const callCushionPct = stressedLVC > 0 ? Math.max(0, 1 - ltv) : 0;
  const marginOfSafety = stressedLVC - balance;
  const maintenanceCall = isMaintenanceCall(balance, stressedLVC, minimumLVC);
  // The uniform drop that would just trigger a call = the pre-stress cushion (§6.11).
  const callThresholdDropPct = preStressLVC > 0 ? Math.max(0, 1 - balance / preStressLVC) * 100 : 0;

  const result: StressSnapshotResult = {
    atMonth,
    stressedPortfolio,
    stressedLVC,
    balance,
    ltv,
    callCushionPct,
    marginOfSafety,
    maintenanceCall,
    callThresholdDropPct,
  };

  if (maintenanceCall) {
    const cure = cureMaintenanceCall(
      cloneBuckets(stressed),
      balance,
      cureTargetLtv,
      combinedRate,
      policy,
    );
    result.cure = { forcedSale: cure.forcedSale, realizedTax: cure.realizedTax };
  }
  return result;
}
